import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, Users, Search, ArrowLeft, Lock } from "lucide-react";
import { Wordmark } from "../components/brand/Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "diallo_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-diallo-charcoal px-5">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 md:p-10 shadow-2xl" data-testid="admin-login-card">
        <Wordmark variant="red" className="h-8" />
        <h1 className="mt-6 font-display font-extrabold text-2xl text-diallo-charcoal flex items-center gap-2">
          <Lock size={22} className="text-diallo-red" /> Espace administrateur
        </h1>
        <p className="mt-1 text-diallo-ink/60 text-sm">Connectez-vous pour consulter les demandes de partenariat.</p>

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-diallo-charcoal">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-diallo-red focus:ring-2 focus:ring-diallo-red/20"
              placeholder="vous@exemple.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-diallo-charcoal">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-diallo-red focus:ring-2 focus:ring-diallo-red/20"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          {error && <p className="text-sm text-diallo-red" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full rounded-xl bg-diallo-red px-6 py-3.5 font-semibold text-white transition-colors hover:bg-diallo-reddeep disabled:opacity-60"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-diallo-ink/50 hover:text-diallo-red">
          <ArrowLeft size={16} /> Retour au site
        </Link>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.get(`${API}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartners(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = partners.filter((p) => {
    const s = q.toLowerCase();
    return (
      p.nom?.toLowerCase().includes(s) ||
      p.quartier?.toLowerCase().includes(s) ||
      p.telephone?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-diallo-sand/40" data-testid="admin-dashboard">
      <header className="bg-diallo-charcoal text-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8 h-16 flex items-center justify-between">
          <Wordmark variant="white" className="h-7" />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/70">{user?.email}</span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20 transition-colors"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-extrabold text-3xl text-diallo-charcoal">Demandes de partenariat</h1>
            <p className="mt-1 text-diallo-ink/60">Les commerçants qui veulent devenir Diallo.</p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 self-start rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-diallo-charcoal shadow-sm hover:shadow transition-shadow"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Actualiser
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-diallo-red p-5 text-white">
            <div className="flex items-center gap-2 text-white/80 text-sm"><Users size={16} /> Total demandes</div>
            <div className="mt-2 font-display font-extrabold text-4xl" data-testid="admin-total-count">{partners.length}</div>
          </div>
          <div className="sm:col-span-2 rounded-2xl bg-white p-5 shadow-sm flex items-center">
            <Search size={18} className="text-diallo-ink/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-search"
              placeholder="Rechercher par nom, quartier ou téléphone…"
              className="ml-3 w-full outline-none bg-transparent text-diallo-ink"
            />
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-partners-table">
              <thead className="bg-diallo-sand/60 text-diallo-charcoal">
                <tr>
                  <th className="px-5 py-3 font-semibold text-sm">Nom</th>
                  <th className="px-5 py-3 font-semibold text-sm">Téléphone</th>
                  <th className="px-5 py-3 font-semibold text-sm">Quartier</th>
                  <th className="px-5 py-3 font-semibold text-sm">Message</th>
                  <th className="px-5 py-3 font-semibold text-sm whitespace-nowrap">Reçu le</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-diallo-ink/50">Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-diallo-ink/50" data-testid="admin-empty">Aucune demande pour le moment.</td></tr>
                ) : (
                  filtered.map((p) => (
                    <tr key={p.id} className="border-t border-black/5 hover:bg-diallo-sand/20">
                      <td className="px-5 py-3 font-medium text-diallo-charcoal">{p.nom}</td>
                      <td className="px-5 py-3 text-diallo-ink/80">
                        <a href={`tel:${p.telephone}`} className="hover:text-diallo-red">{p.telephone}</a>
                      </td>
                      <td className="px-5 py-3 text-diallo-ink/80">{p.quartier}</td>
                      <td className="px-5 py-3 text-diallo-ink/70 max-w-xs truncate" title={p.message}>{p.message || "—"}</td>
                      <td className="px-5 py-3 text-diallo-ink/60 whitespace-nowrap text-sm">{fmtDate(p.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-diallo-charcoal text-white">Chargement…</div>;
  }
  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginView onSuccess={setUser} />;
}
